import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { useAvailableLeadsNumber } from '../../entities/Blitz/useAvailableLeadsNumber';
import { FetchStatus } from '../../entities/utils/useStatesOnFetch';
import { LeadRuleViewModel, LeadRulesViewModel, TurnItOnForOptions } from '../../viewModels/leadrules';
import { MrryDirection, MrryMessageType, MrryModal } from '../MrryModal';
import { AdditionalSettings } from './components/AdditionalSettings';
import { AvailableLeads } from './components/AvailableLeads';
import { BlitzNameFields } from './components/BlitzNameFields';
import { DrawerSection } from './components/DrawerSection';
import { EditFilters } from './components/EditFilters';
import { RuleContainerHeader } from './components/RuleContainerHeader';
import { TurnItOnFor } from './components/TurnItOnFor';
import { styleSheet } from './styles';

interface IProps {
	isNewRule: boolean;
	leadRule: LeadRuleViewModel;
	leadRules: LeadRulesViewModel;
	onRequestClose(result?: LeadRuleViewModel, canceled?: boolean): void;
}

interface IMrryState {
	message: string;
	open: boolean;
}

const mrryInitialState: IMrryState = {
	message: null,
	open: false,
};

export const LeadRuleModal: React.FC<IProps> = observer(({ isNewRule, leadRule, leadRules, onRequestClose }) => {
	const userSession = useUserSession();

	const [selectedTurnItOn, setTurnItOn] = useState<TurnItOnForOptions>(null);

	const [currentLeadRule, setLeadRule] = useState<LeadRuleViewModel>(null);

	const [showMrry, setMrryState] = useState<IMrryState>(mrryInitialState);

	const availableLeads = useAvailableLeadsNumber();

	useEffect(() => {
		if (isNewRule) {
			const newRule = new LeadRuleViewModel(userSession, {
				...(leadRule?.getLeadRule || {}),
				name: `New rule from: ${leadRule?.name} (${Date.now()})`,
			});
			setLeadRule(newRule);
			return;
		}
		setLeadRule(leadRule);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isNewRule, leadRule]);

	useEffect(() => {
		if (currentLeadRule && !isNewRule) {
			availableLeads.get(currentLeadRule?.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentLeadRule]);

	const handleSave = () => {
		if (selectedTurnItOn === TurnItOnForOptions.Everyone) {
			currentLeadRule?.setUsers(null);

			currentLeadRule?.setVerticals(null);
		}
		currentLeadRule[isNewRule ? 'save' : 'update']()
			.then(() => {
				if (isNewRule) {
					leadRules.rules.fetchResults.add(currentLeadRule);
				}
				if (currentLeadRule.id) {
					availableLeads.get(currentLeadRule.id);
				}
			})
			.catch(error => {
				setMrryState({
					message: error?.systemMessage || 'An error ocurred, please try again.',
					open: true,
				});
			})
			.finally(() => leadRules.sort());
	};

	const onClickToClose = () => {
		availableLeads.reset();
		onRequestClose?.(currentLeadRule, true);
	};

	const handleTurnItOn = (option: TurnItOnForOptions) => setTurnItOn(option);

	const handleCloseMrry = () => setMrryState(mrryInitialState);

	return (
		<Dialog open={!!currentLeadRule} onOpenChange={open => !open && onClickToClose()}>
			<DialogPopup styles={[styleSheet.dialog]} dialogCloseStyles={[styleSheet.dialogClose]}>
				<DialogHeader styles={[styleSheet.dialogHeader]}>
					<DialogTitle>{isNewRule ? 'New Blitz' : 'Edit Blitz'}</DialogTitle>
					<AvailableLeads
						availableLeadsLoading={availableLeads.status === FetchStatus.Working}
						availableLeadsNumber={availableLeads.data}
					/>
				</DialogHeader>
				<DialogBody styles={[styleSheet.body]}>
					<div className={css(styleSheet.column)}>
						<DrawerSection title='Blitz'>
							<BlitzNameFields leadRule={currentLeadRule} />
							<RuleContainerHeader leadRule={currentLeadRule} />
						</DrawerSection>
						<AdditionalSettings leadRule={currentLeadRule} />
					</div>
					<div className={css(styleSheet.column)}>
						<EditFilters leadRule={currentLeadRule} />
						<TurnItOnFor leadRule={currentLeadRule} onSelect={handleTurnItOn} />
					</div>
				</DialogBody>
				<DialogFooter styles={[styleSheet.footer]}>
					<Button onClick={handleSave}>Save</Button>
				</DialogFooter>
				{showMrry.open && (
					<MrryModal
						fromDirection={MrryDirection.FromTop}
						message={{
							content: <p>{showMrry.message}</p>,
							duration: 5000,
							type: MrryMessageType.Error,
						}}
						onRequestClose={handleCloseMrry}
					/>
				)}
			</DialogPopup>
		</Dialog>
	);
});
